import { Vector3 } from 'three';

/** 归一化三点面积阈值，避免尺寸很小的有效圆被绝对容差误判。 */
const NORMALIZED_AREA_TOLERANCE = 1e-12;
/** Float32 显示采样允许的相对平面与半径误差。 */
const SAMPLED_CIRCLE_RELATIVE_TOLERANCE = 1e-5;

/**
 * 从有序圆采样点恢复圆心、圆轴和半径，供阵列与基准几何共同使用。
 * 先选择远端弦和面积最大的第三点，再归一化求外接圆；重复闭合端点不会造成退化。
 * 只有所有采样点都落在同一圆上才返回结果，禁止把椭圆或任意空间曲线拟合成圆轴。
 * @param points 同一坐标系中的有序采样点。
 * @returns 圆几何；点不足、非有限、共线或非圆时返回 null。
 */
export function resolveCircularGeometryFromSamplePoints(
  points: readonly Vector3[],
): PuppyCAD.CircularGeometry3D | null {
  if (points.length < 3) return null;
  const origin = points[0];
  let farthestIndex = 0;
  let maxDistanceSq = 0;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(point.z)) return null;
    const distanceSq = point.distanceToSquared(origin);
    if (distanceSq > maxDistanceSq) {
      maxDistanceSq = distanceSq;
      farthestIndex = index;
    }
  }
  if (!(maxDistanceSq > 0) || !Number.isFinite(maxDistanceSq)) return null;

  // 以最长弦归一化，并复用临时向量；扫描过程中不按采样点分配对象。
  const scale = Math.sqrt(maxDistanceSq);
  const firstChord = points[farthestIndex].clone().sub(origin).divideScalar(scale);
  const secondChord = new Vector3();
  const cross = new Vector3();
  let thirdIndex = 0;
  let maxAreaSq = 0;
  for (let index = 1; index < points.length; index += 1) {
    secondChord.copy(points[index]).sub(origin).divideScalar(scale);
    const areaSq = cross.crossVectors(firstChord, secondChord).lengthSq();
    if (areaSq > maxAreaSq) {
      maxAreaSq = areaSq;
      thirdIndex = index;
    }
  }
  if (maxAreaSq <= NORMALIZED_AREA_TOLERANCE) return null;

  // 按曲线参数顺序排列三点，防止选点过程随机翻转圆轴。
  firstChord.copy(points[Math.min(farthestIndex, thirdIndex)]).sub(origin).divideScalar(scale);
  secondChord.copy(points[Math.max(farthestIndex, thirdIndex)]).sub(origin).divideScalar(scale);
  cross.crossVectors(firstChord, secondChord);
  const centerOffset = new Vector3().crossVectors(secondChord, cross).multiplyScalar(firstChord.lengthSq());
  centerOffset.add(new Vector3().crossVectors(cross, firstChord).multiplyScalar(secondChord.lengthSq()));
  centerOffset.multiplyScalar(scale / (2 * cross.lengthSq()));
  const radius = centerOffset.length();
  const center = origin.clone().add(centerOffset);
  const normal = cross.normalize();
  if (!(radius > 0) || !Number.isFinite(radius)
    || !Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(center.z)) return null;

  // 同时校验共面和等半径，防止仅凭三个非共线点接受无效的拓扑采样。
  const tolerance = Math.max(radius * SAMPLED_CIRCLE_RELATIVE_TOLERANCE,
    Number.EPSILON * Math.max(1, Math.abs(center.x), Math.abs(center.y), Math.abs(center.z)) * 32);
  for (const point of points) {
    secondChord.copy(point).sub(center);
    if (Math.abs(secondChord.dot(normal)) > tolerance
      || Math.abs(secondChord.length() - radius) > tolerance) return null;
  }
  return { center, normal, radius };
}
